"use client";
import React, { useEffect, useRef, useState } from "react";
// import PropTypes from "prop-types";
import cn from "classnames";
import Image from "next/image";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import HIWStep from "@/next-src/components/home/how-it-works/hiw-step/HIWStep";

// import ButtonLink from "../../ui/button-link/ButtonLink";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";

import comCSS from "./HowItWorks.module.scss";
import ResponsiveImage from "../../ui/responsive-image/ResponsiveImage";

const COUNTER_STATE = {
  FIXED: "FIXED",
  PINNED_BOTTOM: "PINNED_BOTTOM",
  SCROLL: "SCROLL",
};

const step = 160;

const HowItWorks = ({ className, data = {} }) => {
  const [counterState, setCounterState] = useState(COUNTER_STATE.SCROLL);
  const [counterScroll, setCounterScroll] = useState(0);
  const { width: windowWidth } = useWindowDimensions();

  const timelineRef = useRef();
  const counterMaskRef = useRef();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const steps = document.querySelectorAll(".timeline-step-wrap");
      const changeCounter = () => {
        if (!counterMaskRef?.current || steps.length === 0) return;
        const counterMaskBottom =
          counterMaskRef.current.getBoundingClientRect().bottom - 60;
        let activeIndex = null;
        steps.forEach((step, index) => {
          const { top: stepTop } = step.getBoundingClientRect();
          if (stepTop <= counterMaskBottom) {
            activeIndex = index;
          }
        });
        const curElBox = steps[activeIndex]?.getBoundingClientRect();
        const scrollValue = Math.min(
          Math.max(counterMaskBottom - curElBox?.top || 0, 0),
          step
        );
        setCounterScroll(
          -1 * ((scrollValue / step) * 10 + 10 * (activeIndex - 1))
        );
      };

      const onScroll = () => {
        if (!timelineRef?.current) return;

        const { top, bottom } = timelineRef.current.getBoundingClientRect();

        const isInView = top <= 200 && bottom >= window.innerHeight - 100;
        if (isInView) {
          setCounterState(COUNTER_STATE.FIXED);
        } else if (bottom < window.innerHeight - 100) {
          setCounterState(COUNTER_STATE.PINNED_BOTTOM);
        } else {
          setCounterState(COUNTER_STATE.SCROLL);
        }
        changeCounter();
      };

      window.addEventListener("scroll", onScroll, false);
      window.addEventListener("resize", onScroll, false);

      // Call handler right away so state gets updated with initial window size
      onScroll();

      return () => {
        window.removeEventListener("scroll", onScroll, false);
        window.removeEventListener("resize", onScroll, false);
      };
    }
  }, []);

  const stepsCount = data?.items?.filter((el) => !el.hide)?.length || 0;

  return (
    <section
      className={cn(className, comCSS.wrapper, "section")}
      id="process"
      data-w-id="ed26eb4c-426c-fcdf-852c-1e56420ee264"
      style={{
        backgroundColor:
          windowWidth >= 992 ? data.bgColor || "" : data?.bgColorMobile || "",
      }}
    >
      <div className="padding-global">
        <div className="container-large">
          <div className="padding-section-large">
            <div className="margin-bottom margin-xlarge">
              <div className="section_heading-wrapper _1fr-1fr-mobile-off">
                <div className="section_heading">
                  <div className="max-width-large">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-note hide-tablet">
                        {convertHTMLtoJSX(data?.preTitle)}
                      </div>
                      <div className="text-note hide-desktop">
                        {convertHTMLtoJSX(data?.preTitleMobile)}
                      </div>
                    </div>
                    <div className="margin-bottom hide-tablet margin-xsmall">
                      {convertHTMLtoJSX(data?.title)}
                    </div>
                    <div className="margin-bottom hide-desktop margin-xsmall">
                      {convertHTMLtoJSX(data?.titleMobile)}
                    </div>
                    <div className="text-size-medium hide-tablet">
                      {convertHTMLtoJSX(data?.text)}
                    </div>
                    <div className="text-size-medium hide-desktop">
                      {convertHTMLtoJSX(data?.textMobile)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div ref={timelineRef} className="timeline-wrap">
              <div className="timeline-half hide-tablet">
                <div
                  ref={counterMaskRef}
                  className={cn("timeline-counter-mask", comCSS.counterMask, {
                    [comCSS.counterFixed]: counterState === COUNTER_STATE.FIXED,
                    [comCSS.counterPinnedBottom]:
                      counterState === COUNTER_STATE.PINNED_BOTTOM,
                  })}
                >
                  <div className="timeline-counter-number">0</div>
                  <div
                    className="timeline-counter-numbers"
                    style={{
                      willChange: "transform",
                      transform: `translate3d(0px, ${counterScroll}rem, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)`,
                      transformStyle: "preserve-3d",
                    }}
                  >
                    {data?.items
                      ?.filter((el) => !el.hide)
                      ?.map((item, index) => (
                        <div
                          className="timeline-counter-number"
                          key={`##${item.id}`}
                        >
                          {index + 1}
                        </div>
                      ))}
                  </div>
                </div>
                <div
                  className={cn(
                    "timeline-picture-wrap",
                    comCSS.timelinePictureWrap
                  )}
                >
                  {data?.items
                    ?.filter((el) => !el.hide)
                    ?.map((item) => (
                      <div
                        className="timeline-pictures-inner"
                        key={`pictures-${item.id}`}
                      >
                        {item.images.map((el) => (
                          <div
                            key={`picture-${el.src}`}
                            className={cn(
                              "timeline-picture-mask",
                              el.orientation
                            )}
                          >
                            <ResponsiveImage
                              width={256}
                              height={352}
                              src={el.src}
                              lazyLoading
                              sizes="(max-width: 479px) 0px, (max-width: 991px) 100vw, 16vw"
                              alt={el.alt}
                              className="cover-image"
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                </div>
              </div>

              <div
                data-w-id="24a44dbe-7511-9462-7300-abf77a0182b6"
                className="timeline-half"
              >
                {data?.items
                  ?.filter((el) => !el.hide)
                  ?.map((el, i) => (
                    <HIWStep
                      data={{ ...el, stepNumber: `0${i + 1}`, stepsCount }}
                      key={el.id}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

HowItWorks.propTypes = {};

export default HowItWorks;
